import React, { useEffect, useState } from "react";
import {
  Button,
  Grid,
  Pagination,
  Paper,
  Stack,
  Typography,
  Box,
  Card,
  Divider,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import TableItem from "../../../components/TableItem";
import { getImportData, DiactivateItemById } from "../../../services/Import";
import ImportFrom from "./ImportFrom";
import CircularProgress from "@mui/material/CircularProgress";
import { useHistory } from "react-router";
import InputField from "../../../FormComponents/InputField";
import Im_CategorySelectingPopup from "../../../components/Im_CategorySelectingPopup";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import InventoryIcon from "@mui/icons-material/Inventory";
import BalanceIcon from "@mui/icons-material/Balance";
import ImportBulkUpdate from "./ImportBulkUpdate";
import StockReconciliation from "./StockReconciliation";
import { RoleBasedRender } from "../../../components/RoleBaseRender";

// Custom styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(3),
  boxShadow: "0 8px 16px 0 rgba(0,0,0,0.1)",
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.1)",
}));

const FilterContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.5,
  backgroundColor: alpha(theme.palette.primary.light, 0.05),
  marginBottom: theme.spacing(3),
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

let _imItemListState = null;

function Im_Items() {
  const history = useHistory();

  // State management
  const [openDisableWarning, setOpenDisableWarning] = useState(false);
  const [disableItemId, setDisableItemId] = useState(null);
  const [disableItemName, setDisableItemName] = useState("");
  const [confirmName, setConfirmName] = useState("");
  const [rows, setRows] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openForm, setOpenForm] = useState(false);
  const [openBulkUpdateForm, setOpenBulkUpdateForm] = useState(false);
  const [openReconciliationForm, setOpenReconciliationForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // For category popup
  const [openCatgoryTable, setOpenCatgoryTable] = useState(false);
  const [selectedCatgory, setSelectedCatgory] = useState([]);
  const [catgoryRows, setCatgoryRows] = useState([]);

  // Table columns configuration
  const columns = [
    { id: "code", label: "Code", minWidth: 80 },
    { id: "item", label: "Item", minWidth: 180 },
    { id: "brand", label: "Brand", minWidth: 120 },
    {
      id: "qty",
      label: "Quantity",
      minWidth: 100,
      format: (value) => value.toLocaleString("en-US"),
    },
    {
      id: "status",
      label: "Status",
      minWidth: 120,
      format: (value) => (
        <Chip
          label={value}
          color={
            value === "Active"
              ? "success"
              : value === "Pending"
                ? "warning"
                : "error"
          }
          size="small"
          variant="outlined"
        />
      ),
    },
    { id: "action", label: "Actions", minWidth: 120 },
  ];

  // Create data row factory function
  function createData(code, item, brand, qty, status, action, im_id) {
    return { code, item, brand, qty, status, action, im_id };
  }

  // Filter state
  const [filter, setFilter] = useState({
    _productCode: "",
    _itemName: "",
    _brand: "",
    _addedBy: "",
    _category: null,
    _unitType: "",
    _status: "",
  });

  // Initial data load
  useEffect(() => {
    if (_imItemListState) {
      const { page: savedPage, filter: savedFilter } = _imItemListState;
      _imItemListState = null;
      setPage(savedPage);
      setFilter(savedFilter);
      fetchData(savedFilter, savedPage);
    } else {
      fetchData(filter, page);
    }
  }, []);

  // Pagination handler
  const handleChange = async (event, value) => {
    setPage(value - 1);
    await fetchData(filter, value - 1);
  };

  // Fetch data from API
  const fetchData = async (itemFilter, pageNo) => {
    setLoading(true);
    try {
      let result = await getImportData(pageNo, rowsPerPage, itemFilter);
      let importSet = result.content;

      // Set total rows and pages
      setTotalPages(result.totalPages);
      setTotalRows(result.totalElements);

      const newSet = [];
      if (importSet) {
        for (let x = 0; x < importSet.length; x++) {
          newSet.push(
            createData(
              importSet[x].product_code,
              importSet[x].itemName,
              importSet[x].brand,
              importSet[x].qty,
              importSet[x].status,
              importSet[x].importId,
            ),
          );
        }
      }

      setRows(newSet);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Action handler for table actions
  const handleAction = async (event, id) => {
    switch (event) {
      case "view":
        _imItemListState = { page, filter };
        history.push("/template/im_item_view/" + id);
        break;
      case "edit":
        history.push("/template/im_item_edit/" + id);
        break;
      case "disable":
        disableProduct(id);
        break;
      default:
        return;
    }
  };

  // Disable product confirmation
  const disableProduct = (id) => {
    const row = rows.find((r) => r.action === id);
    setDisableItemName(row ? row.item : "");
    setConfirmName("");
    setDisableItemId(id);
    setOpenDisableWarning(true);
  };

  // Reset filters
  const resetFilters = () => {
    setFilter({
      _productCode: "",
      _itemName: "",
      _brand: "",
      _addedBy: "",
      _category: null,
      _unitType: "",
      _status: "",
    });
  };

  // Handle filter change
  const handleFilterChange = (name, val) => {
    setFilter({
      ...filter,
      [name]: val,
    });
  };

  // Toggle filter visibility
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  // handle categories
  const handleOpenCategoryTable = async () => {
    setOpenCatgoryTable(true);
  };

  const handleCloseCatgoryTable = async () => {
    setOpenCatgoryTable(false);
    setFilter({
      ...filter,
      _category: selectedCatgory,
    });
  };

  return (
    <>
      <Dialog open={openDisableWarning} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Deactivation</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This will deactivate <strong>{disableItemName}</strong>. It will no
            longer appear in selection lists across the system.
          </DialogContentText>
          <DialogContentText sx={{ mb: 1 }}>
            Type the item name to confirm:
          </DialogContentText>
          <TextField
            fullWidth
            size="small"
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            placeholder={disableItemName}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              setOpenDisableWarning(false);
              setConfirmName("");
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={confirmName !== disableItemName}
            onClick={async () => {
              await DiactivateItemById(disableItemId);
              setOpenDisableWarning(false);
              setConfirmName("");
              await fetchData(filter, page);
            }}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
      <StyledPaper elevation={0}>
        {openForm ? (
          <ImportFrom setOpenForm={setOpenForm} />
        ) : openBulkUpdateForm ? (
          <ImportBulkUpdate setOpenBulkUpdateForm={setOpenBulkUpdateForm} />
        ) : openReconciliationForm ? (
          <StockReconciliation
            setOpenReconciliationForm={setOpenReconciliationForm}
          />
        ) : (
          <>
            <HeaderCard>
              <Grid container alignItems="center" spacing={2}>
                <Grid item>
                  <InventoryIcon fontSize="large" />
                </Grid>
                <Grid item xs>
                  <Typography variant="h5" fontWeight="bold">
                    Import Inventory
                  </Typography>
                  <Typography variant="body2">
                    View and manage all imported inventory items
                  </Typography>
                </Grid>
                <Grid item>
                  <RoleBasedRender
                    allowedRoles={["INVENTORY_CREATE", "ROLE_ADMIN"]}
                  >
                    <ActionButton
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={() => setOpenForm(true)}
                    >
                      Add Import
                    </ActionButton>
                  </RoleBasedRender>
                </Grid>
                <Grid item>
                  <RoleBasedRender
                    allowedRoles={["INVENTORY_CREATE", "ROLE_ADMIN"]}
                  >
                    <ActionButton
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={() => setOpenBulkUpdateForm(true)}
                    >
                      Bulk Import
                    </ActionButton>
                  </RoleBasedRender>
                </Grid>
                <Grid item>
                  <RoleBasedRender
                    allowedRoles={["INVENTORY_UPDATE", "ROLE_ADMIN"]}
                  >
                    <ActionButton
                      variant="outlined"
                      color="primary"
                      startIcon={<BalanceIcon />}
                      onClick={() => setOpenReconciliationForm(true)}
                    >
                      Reconcile Stock
                    </ActionButton>
                  </RoleBasedRender>
                </Grid>
              </Grid>
            </HeaderCard>

            <Box
              mb={3}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <TextField
                placeholder="Search by item name or code..."
                variant="outlined"
                size="small"
                sx={{ width: "40%" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => {
                  handleFilterChange("_itemName", e.target.value);
                  if (
                    e.target.value.length > 2 ||
                    e.target.value.length === 0
                  ) {
                    fetchData({ ...filter, _itemName: e.target.value }, page);
                  }
                }}
              />
              <Stack direction="row" spacing={2}>
                <Tooltip title="Toggle Filters">
                  <ActionButton
                    variant="outlined"
                    color="primary"
                    startIcon={<FilterListIcon />}
                    onClick={toggleFilters}
                  >
                    Filters
                  </ActionButton>
                </Tooltip>
                <Chip
                  label={`${totalRows} Records`}
                  variant="outlined"
                  color="primary"
                />
              </Stack>
            </Box>

            {showFilters && (
              <FilterContainer>
                <Typography variant="subtitle2" fontWeight="bold" mb={2}>
                  Advanced Filters
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={2}>
                    <InputField
                      name="_productCode"
                      value={filter._productCode}
                      onChange={(event, newInputValue) =>
                        handleFilterChange(event, newInputValue)
                      }
                      type="text"
                      label="Product Code"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <InputField
                      name="_brand"
                      value={filter._brand}
                      onChange={(event, newInputValue) =>
                        handleFilterChange(event, newInputValue)
                      }
                      type="text"
                      label="Brand"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <InputField
                      name="_unitType"
                      value={filter._unitType}
                      onChange={(event, newInputValue) =>
                        handleFilterChange(event, newInputValue)
                      }
                      type="text"
                      label="Unit Type"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <InputField
                      name="_addedBy"
                      value={filter._addedBy}
                      onChange={(event, newInputValue) =>
                        handleFilterChange(event, newInputValue)
                      }
                      type="text"
                      label="Added By"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <InputField
                      name="_status"
                      value={filter._status}
                      onChange={(event, newInputValue) =>
                        handleFilterChange(event, newInputValue)
                      }
                      type="text"
                      label="Status"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <ActionButton
                      variant="outlined"
                      color="primary"
                      fullWidth
                      size="medium"
                      onClick={handleOpenCategoryTable}
                    >
                      Select Category
                    </ActionButton>
                  </Grid>
                </Grid>
                <Box mt={2} display="flex" justifyContent="flex-end">
                  <Stack direction="row" spacing={2}>
                    <Tooltip title="Reset Filters">
                      <ActionButton
                        variant="outlined"
                        color="secondary"
                        startIcon={<RestartAltIcon />}
                        onClick={() => {
                          resetFilters();
                          fetchData({}, page);
                        }}
                      >
                        Reset
                      </ActionButton>
                    </Tooltip>
                    <Tooltip title="Apply Filters">
                      <ActionButton
                        variant="contained"
                        color="primary"
                        onClick={() => fetchData(filter, page)}
                      >
                        Apply Filters
                      </ActionButton>
                    </Tooltip>
                  </Stack>
                </Box>
              </FilterContainer>
            )}

            <Divider sx={{ my: 2 }} />

            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                <CircularProgress />
              </Box>
            ) : rows.length === 0 ? (
              <Box sx={{ textAlign: "center", p: 5 }}>
                <Typography variant="body1" color="textSecondary">
                  No import items found. Try adjusting your filters or add a new
                  import.
                </Typography>
              </Box>
            ) : (
              <TableItem
                columns={columns}
                rows={rows}
                page={page}
                setPage={setPage}
                tablePagin={false}
                totalPages={totalPages}
                handleAction={handleAction}
                showActions={{
                  view: "View item",
                  edit: "Edit item",
                  disable: "Deactivate Item",
                }}
                actionRoles={{
                  view: ["INVENTORY_VIEW", "ROLE_ADMIN"],
                  edit: ["INVENTORY_UPDATE", "ROLE_ADMIN"],
                  disable: ["INVENTORY_DELETE", "ROLE_ADMIN"],
                }}
                tableProps={{
                  sx: {
                    "& .MuiTableRow-root:hover": {
                      backgroundColor: alpha("#000", 0.04),
                    },
                  },
                }}
              />
            )}

            {totalPages > 1 && (
              <PaginationContainer>
                <Pagination
                  count={totalPages}
                  page={page + 1}
                  variant="outlined"
                  shape="rounded"
                  color="primary"
                  onChange={handleChange}
                  size="medium"
                />
              </PaginationContainer>
            )}
          </>
        )}
      </StyledPaper>

      {openCatgoryTable && (
        <Im_CategorySelectingPopup
          setOpenCatgoryTable={setOpenCatgoryTable}
          openCatgoryTable={openCatgoryTable}
          handleCloseCatgoryTable={handleCloseCatgoryTable}
          setSelectedCatgory={setSelectedCatgory}
          selectedCatgory={selectedCatgory}
          setCatgoryRows={setCatgoryRows}
          catgoryRows={catgoryRows}
        />
      )}
    </>
  );
}

export default Im_Items;
